import { Link } from "react-router-dom";
import { PhoneCall, Mail, ShieldCheck, ClipboardCheck, FileText, Rocket, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskLines, Reveal, SectionHead } from "@/components/Reveal";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "@/data";

const STEPS = [
  { icon: ClipboardCheck, title: "Free risk assessment", text: "We visit your Welsh site, walk the perimeter, review incident history and listen to your concerns — no charge, no obligation." },
  { icon: FileText, title: "Written security plan", text: "You receive site-specific assignment instructions, honest recommendations and a fixed quote — a plan we can be held to." },
  { icon: Rocket, title: "Deployment within 24 hours", text: "Vetted, SIA-licensed officers briefed on your site instructions. Urgent cover can typically be in place within a day, anywhere in Wales." },
];

const Contact = () => (
  <div data-testid="contact-page">
    <Seo
      title="Contact Xtreme Protection Wales | 24/7 Security Line +44 2046 402840"
      description="Contact Xtreme Protection for SIA-licensed security services across Wales. Call +44 2046 402840 (24/7) or email admin@xtremeprotection.uk. Free risk assessment, deployment within 24 hours."
      path="/contact"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Xtreme Protection — Wales Security Services",
        about: { "@type": "LocalBusiness", name: "Xtreme Protection — Wales Security Services", telephone: "+44 2046 402840", email: "admin@xtremeprotection.uk" },
      }}
    />

    <section className="border-b border-[#1C2027] bg-[#0D1015] pb-16 pt-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="eyebrow flex items-center gap-3">
          <span className="h-px w-10 bg-[#F5A800]" />
          Contact Command // 24/7 Response
        </p>
        <MaskLines className="font-condensed mt-6 text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-7xl" lines={["Speak To The", "Wales Command Team"]} />
        <Reveal delay={0.3}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9BA1AB] sm:text-lg">
            No forms, no call queues, no ticket systems. When you contact Xtreme Protection you speak directly with the team that will run your contract — day or night, anywhere in Wales.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
      <div className="grid gap-6 md:grid-cols-2" data-testid="contact-channels">
        <Reveal>
          <a href={PHONE_HREF} data-testid="contact-phone-card" className="group flex h-full flex-col justify-between border border-[#1C2027] bg-[#10131A] p-8 transition-colors duration-300 hover:border-[#F5A800]/60 sm:p-10">
            <div>
              <span className="flex h-14 w-14 items-center justify-center border border-[#F5A800]/40 bg-[#F5A800]/10 transition-colors duration-300 group-hover:bg-[#F5A800]">
                <PhoneCall className="h-7 w-7 text-[#F5A800] transition-colors duration-300 group-hover:text-[#0A0C10]" />
              </span>
              <h2 className="font-condensed mt-6 text-3xl font-bold uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-[#F5A800]">Call the command line</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#9BA1AB]">24 hours a day, 365 days a year. Emergencies, urgent deployments, contract queries and free risk assessments.</p>
            </div>
            <p className="font-condensed mt-8 text-3xl font-bold tracking-wide text-[#F5A800]">{PHONE}</p>
          </a>
        </Reveal>
        <Reveal delay={0.1}>
          <a href={EMAIL_HREF} data-testid="contact-email-card" className="group flex h-full flex-col justify-between border border-[#1C2027] bg-[#10131A] p-8 transition-colors duration-300 hover:border-[#F5A800]/60 sm:p-10">
            <div>
              <span className="flex h-14 w-14 items-center justify-center border border-[#F5A800]/40 bg-[#F5A800]/10 transition-colors duration-300 group-hover:bg-[#F5A800]">
                <Mail className="h-7 w-7 text-[#F5A800] transition-colors duration-300 group-hover:text-[#0A0C10]" />
              </span>
              <h2 className="font-condensed mt-6 text-3xl font-bold uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-[#F5A800]">Email the office</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#9BA1AB]">Quotes, site details, tender documents and contract paperwork. We reply within working hours — usually much faster.</p>
            </div>
            <p className="mt-8 break-all text-xl font-bold tracking-wide text-[#F5A800]">{EMAIL}</p>
          </a>
        </Reveal>
      </div>
    </section>

    <section className="border-t border-[#1C2027] bg-[#0D1015] py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead index="01" eyebrow="The Process" title="What happens when you call" testid="contact-process-heading" />
        <div className="mt-10 grid gap-px border border-[#1C2027] bg-[#1C2027] md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} y={16} className="h-full">
              <div className="flex h-full flex-col bg-[#10131A] p-7" data-testid={`contact-step-${i}`}>
                <div className="flex items-center justify-between">
                  <s.icon className="h-6 w-6 text-[#F5A800]" />
                  <span className="font-condensed text-sm font-bold text-white/30">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-condensed mt-5 text-xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#9BA1AB]">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
      <div className="grid gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="border border-[#1C2027] bg-[#10131A] p-8" data-testid="contact-coverage-card">
            <ShieldCheck className="h-8 w-8 text-[#F5A800]" />
            <h2 className="font-condensed mt-5 text-2xl font-bold uppercase tracking-tight text-white">Covering all of Wales</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#9BA1AB]">
              35+ towns and cities across North, South, Mid and West Wales — every one served by local officers backed by our 24/7 command centre. Every one of our 17 specialist services deploys in every location we cover.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/locations" data-testid="contact-locations-link" className="group flex items-center gap-2 border border-[#F5A800]/50 px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-[#F5A800] transition-colors duration-300 hover:bg-[#F5A800] hover:text-[#0A0C10]">
                All Locations <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/services" data-testid="contact-services-link" className="group flex items-center gap-2 border border-[#1C2027] px-4 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-white/70 transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]">
                All Services <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex h-full flex-col justify-center border border-[#F5A800]/30 bg-[#F5A800]/5 p-8" data-testid="contact-emergency-card">
            <p className="eyebrow">Urgent requirement?</p>
            <h2 className="font-condensed mt-3 text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Break-in, incident or lapsed cover?</h2>
            <p className="mt-3 text-sm leading-relaxed text-[#9BA1AB]">
              If your site is at risk right now, don't email — call. Our command centre can have a vetted, uniformed officer on your premises within hours, day or night.
            </p>
            <a href={PHONE_HREF} data-testid="contact-emergency-call-btn" className="mt-6 inline-flex w-fit items-center gap-2 bg-[#F5A800] px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
              <PhoneCall className="h-4 w-4" />
              Call {PHONE} now
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Contact;
